import React, { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Card,
  CardContent,
  Avatar,
  Container,
  Grid,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  getMostPopularPlan,
  getClientReports,
  getAssetStatistics,
  getSupportLogs,
} from "../../../Services/ReportsApi";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import StorageIcon from "@mui/icons-material/Storage";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import DescriptionIcon from "@mui/icons-material/Description";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import OnlineOrderTanentReports from "./OnlineOrderTanentReports";

const ReportsPage = () => {
  const [mostPopularPlan, setMostPopularPlan] = useState(null);
  const [clientReports, setClientReports] = useState([]);
  const [assetStatistics, setAssetStatistics] = useState([]);
  const [supportLogs, setSupportLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState("popular");
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: ""
  });

  useEffect(() => {
    Promise.all([
      getMostPopularPlan(),
      getClientReports(),
      getAssetStatistics(),
      getSupportLogs(),
    ])
      .then(([popularPlanRes, clientReportsRes, assetStatsRes, supportLogsRes]) => {
        setMostPopularPlan(popularPlanRes.data);
        setClientReports(clientReportsRes.data);
        setAssetStatistics(assetStatsRes.data);
        setSupportLogs(supportLogsRes.data);
      })
      .catch(() => {
        setMostPopularPlan(null);
        setClientReports([]);
        setAssetStatistics([]);
        setSupportLogs([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleReportTypeChange = (event, newType) => {
    if (newType !== null) {
      setReportType(newType);
    }
  };

  const handleClearDateFilter = () => {
    setDateFilter({
      startDate: "",
      endDate: ""
    });
  };

  // فلترة البيانات حسب التاريخ
  const filterByDate = (data, dateField) => {
    if (!dateFilter.startDate && !dateFilter.endDate) {
      return data;
    }

    return data.filter((item) => {
      const itemDate = new Date(item[dateField]);
      itemDate.setHours(0, 0, 0, 0);

      let matches = true;

      if (dateFilter.startDate) {
        const startDate = new Date(dateFilter.startDate);
        startDate.setHours(0, 0, 0, 0);
        matches = matches && itemDate >= startDate;
      }

      if (dateFilter.endDate) {
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        matches = matches && itemDate <= endDate;
      }

      return matches;
    });
  };

  // تطبيق الفلتر على البيانات
  const filteredClientReports = filterByDate(clientReports, 'created_at');
  const filteredSupportLogs = filterByDate(supportLogs, 'date');

  const isDateFilterActive = dateFilter.startDate || dateFilter.endDate;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>جاري تحميل التقارير...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#667eea', width: 50, height: 50 }}>
              <AssessmentIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                التقارير والإحصائيات
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                نظرة شاملة على أداء النظام
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Report Type Filter */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                <AssessmentIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                اختر نوع التقرير:
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={handleReportTypeChange}
              aria-label="report type"
              sx={{ 
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiToggleButton-root': {
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  border: '2px solid',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="popular" sx={{ borderColor: '#ff9800 !important' }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                الخطة الأكثر شيوعًا
              </ToggleButton>
              <ToggleButton value="clients" sx={{ borderColor: '#2196f3 !important' }}>
                <PeopleIcon sx={{ mr: 1 }} />
                تقارير العملاء
              </ToggleButton>
              <ToggleButton value="assets" sx={{ borderColor: '#9c27b0 !important' }}>
                <StorageIcon sx={{ mr: 1 }} />
                إحصائيات الاستهلاك من قاعدة البيانات
              </ToggleButton>
              <ToggleButton value="support" sx={{ borderColor: '#4caf50 !important' }}>
                <SupportAgentIcon sx={{ mr: 1 }} />
                سجلات الدعم
              </ToggleButton>
              <ToggleButton value="onlineOrders" sx={{ borderColor: '#ff5722 !important' }}>
                <AssessmentIcon sx={{ mr: 1 }} />
                طلبات الأونلاين الخاصة بالنسخ
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Date Filter - يظهر لجميع التقارير ما عدا "الخطة الأكثر شيوعاً" */}
      {reportType !== "popular" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, bgcolor: '#fff3e0', border: '2px solid #ffe0b2' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Avatar sx={{ bgcolor: '#ff9800' }}>
                <FilterListIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                فلترة حسب التاريخ:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                  <DateRangeIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    من:
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                    sx={{ 
                      bgcolor: 'white',
                      minWidth: 150
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                  <DateRangeIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    إلى:
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                    sx={{ 
                      bgcolor: 'white',
                      minWidth: 150
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
                {isDateFilterActive && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="مسح فلتر التاريخ">
                      <IconButton
                        onClick={handleClearDateFilter}
                        size="small"
                        sx={{ 
                          bgcolor: '#ffebee',
                          '&:hover': {
                            bgcolor: '#ffcdd2'
                          }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>
            {isDateFilterActive && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {dateFilter.startDate && (
                  <Chip 
                    label={`من: ${new Date(dateFilter.startDate).toLocaleDateString('ar')}`}
                    color="warning"
                    onDelete={() => setDateFilter(prev => ({ ...prev, startDate: "" }))}
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                {dateFilter.endDate && (
                  <Chip 
                    label={`إلى: ${new Date(dateFilter.endDate).toLocaleDateString('ar')}`}
                    color="warning"
                    onDelete={() => setDateFilter(prev => ({ ...prev, endDate: "" }))}
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Most Popular Plan */}
      {reportType === "popular" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, animation: 'fadeIn 0.5s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}>
                <TrendingUpIcon />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                الخطة الأكثر شيوعًا
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {mostPopularPlan ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocalOfferIcon sx={{ color: '#ff9800' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                          {mostPopularPlan.plan.plane_name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {mostPopularPlan.plan.desc}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          icon={<AttachMoneyIcon />}
                          label={`شهري: ${mostPopularPlan.plan.monthly_price} جنيه`}
                          color="primary"
                          size="small"
                        />
                        <Chip 
                          icon={<AttachMoneyIcon />}
                          label={`سنوي: ${mostPopularPlan.plan.yearly_price} جنيه`}
                          color="secondary"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <TrendingUpIcon sx={{ fontSize: 60, color: '#4caf50', mb: 1 }} />
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {mostPopularPlan.total}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        إجمالي الاشتراكات
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <LocalOfferIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 1 }} />
                <Typography color="text.secondary">لا توجد بيانات متوفرة</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Client Reports */}
      {reportType === "clients" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, animation: 'fadeIn 0.5s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40 }}>
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                  تقارير العملاء
                </Typography>
              </Box>
              <Chip 
                label={`${filteredClientReports.length} عميل`}
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {filteredClientReports.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم العميل</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>البريد الإلكتروني</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>رقم الهاتف</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم المستأجر</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredClientReports.map((client) => (
                      <TableRow 
                        key={client.id}
                        sx={{ '&:hover': { bgcolor: '#f9f9f9', transition: 'all 0.2s' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {client.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ color: '#666', fontSize: 18 }} />
                            <Typography variant="body2">
                              {client.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ color: '#666', fontSize: 18 }} />
                            <Chip 
                              label={client.phone}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={client.tenant?.id || "غير متوفر"}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <PeopleIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 1 }} />
                <Typography color="text.secondary">
                  {isDateFilterActive ? "لا توجد نتائج للفترة المحددة" : "لا توجد تقارير عملاء"}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Asset Statistics */}
      {reportType === "assets" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, animation: 'fadeIn 0.5s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40 }}>
                  <StorageIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                  إحصائيات الأصول
                </Typography>
              </Box>
              <Chip 
                label={`${assetStatistics.length} مستأجر`}
                color="secondary"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {assetStatistics.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم المستأجر</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الحجم</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assetStatistics.map((asset) => (
                      <TableRow 
                        key={asset.tenant_id}
                        sx={{ '&:hover': { bgcolor: '#f9f9f9', transition: 'all 0.2s' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32 }}>
                              <StorageIcon fontSize="small" />
                            </Avatar>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {asset.tenant_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${asset.size_mb} MB`}
                            color="secondary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <StorageIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 1 }} />
                <Typography color="text.secondary">لا توجد إحصائيات أصول</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Support Logs */}
      {reportType === "support" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, animation: 'fadeIn 0.5s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                  <SupportAgentIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                  سجلات الدعم الفني
                </Typography>
              </Box>
              <Chip 
                label={`${filteredSupportLogs.length} سجل`}
                color="success"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {filteredSupportLogs.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>التاريخ</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الإجراء</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>المحتوى</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم المستخدم</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSupportLogs.map((log) => (
                      <TableRow 
                        key={log.id}
                        sx={{ '&:hover': { bgcolor: '#f9f9f9', transition: 'all 0.2s' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EventIcon sx={{ color: '#666', fontSize: 18 }} />
                            <Typography variant="body2">
                              {log.date}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={log.action}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DescriptionIcon sx={{ color: '#666', fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                              {log.action_content}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: '#4caf50', width: 28, height: 28 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {log.user?.name || "غير متوفر"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <SupportAgentIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 1 }} />
                <Typography color="text.secondary">
                  {isDateFilterActive ? "لا توجد نتائج للفترة المحددة" : "لا توجد سجلات دعم"}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Online Orders Tab */}
      {reportType === "onlineOrders" && (
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar sx={{ bgcolor: '#ff5722', width: 40, height: 40 }}>
                <AssessmentIcon />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                طلبات الأونلاين الخاصة بالنسخ
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <OnlineOrderTanentReports />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default ReportsPage;